// src/components/detail/FeedDetailDialog.tsx
// Minimal dark analysis report popup used by both MainPage and FeedList.
import React, { useMemo, useState } from "react";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import AddAlertOutlined from "@mui/icons-material/AddAlertOutlined";

import dayjs from "dayjs";

import { FeedDetailItem } from "../../types/FeedItem";
import "./feedDetailDialog.dark.css";

interface FeedDetailDialogProps {
  open: boolean;
  onClose: () => void;
  feed?: FeedDetailItem | null;
  loading?: boolean;
  error?: string | null;
  onAlert?: (eventId: string) => Promise<any> | void;
}

type CandidateGroup = "direct" | "review" | "watch";
type Tone = "direct" | "review" | "watch" | "risk" | "neutral" | "positive";

type StockCandidate = {
  name?: string;
  corp_name?: string;
  corpName?: string;
  ticker?: string;
  stock_code?: string;
  stockCode?: string;
  code?: string;
  tier?: string;
  mapping_bucket?: string;
  mappingBucket?: string;
  mapping_decision?: string;
  mappingDecision?: string;
  decision?: string;
  relation_tier?: string;
  reason?: string;
  reason_code?: string;
  role?: string;
  match_type?: string;
  matchType?: string;
  matched_terms?: any;
  matchedTerms?: any;
  strong_terms?: any;
  strongTerms?: any;
  support_terms?: any;
  supportTerms?: any;
  user_visible?: boolean;
  is_direct_stock_mapping?: boolean;
  isDirectStockMapping?: boolean;
  score?: number;
  mapping_score?: number;
  final_score?: number;
  [key: string]: any;
};

type CandidateBuckets = {
  direct: StockCandidate[];
  review: StockCandidate[];
  watch: StockCandidate[];
  visible: StockCandidate[];
};

type DetailVM = {
  title: string;
  summary: string;
  source: string;
  dateLabel: string;
  sourceUrl: string;
  impactScore: number;
  impactDirection: "positive" | "negative" | "neutral";
  impactLabel: string;
  impactReason: string;
  modeLabel: string;
  sectorLabel: string;
  canonicalSector: string;
  tags: string[];
  buckets: CandidateBuckets;
};



const INTERNAL_REASON_CODES = new Set([
  "EVENT_UNIT_PUBLIC_REVIEW_BLOCKED_BY_WEAK_ENTITY_EVIDENCE",
  "AMBIGUOUS_ACTOR_COMPANY_MATCH",
  "EVENT_UNIT_STOCK_MAPPING_DISABLED",
  "POLICY_EVENT_EXCLUDE_CAPPED",
  "POLICY_EVENT_WATCH_CAPPED",
  "POLICY_EVENT_REVIEW_CAPPED",
]);

function parseJsonLoose(value: any): any {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function cleanText(value: any): string {
  return String(value ?? "")
    .replace(/&amp;/g, "&")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[#"`~!@$^&*_=+|\\<>[\]{}]/g, " ")
    .replace(/\s*[·•▪︎▶◆◇■□※→←↑↓↔]+\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function firstNonEmpty(...values: any[]): any {
  for (const value of values) {
    if (cleanText(value)) return value;
  }
  return "";
}


function asArrayLoose(value: any): any[] {
  const parsed = parseJsonLoose(value);
  const source = parsed ?? value;
  if (Array.isArray(source)) return source;
  return [];
}

function normalizeTicker(value: any): string | undefined {
  const raw = String(value ?? "").trim().toUpperCase();
  if (!raw) return undefined;
  const code = raw.match(/^A?(\d{6})$/)?.[1] ?? raw.match(/\b(\d{6})\b/)?.[1];
  if (!code || code === "000000" || code === "999999") return undefined;
  return code;
}

function norm(value: any): string {
  return cleanText(value)
    .replace(/[\s_\-/]+/g, " ")
    .toLowerCase();
}

function clamp100(value: any): number {
  if (typeof value === "string") {
    const m = value.match(/-?\d+(?:\.\d+)?/);
    if (m) value = Number(m[0]);
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

function isInternalLine(value: any): boolean {
  const text = cleanText(value);
  const normalized = norm(text);
  if (!text) return true;
  if (/source_classification|event_final_mode|event_mapping_status|mapping_status|candidate_summary|candidate_recall|ticker_buckets|debug|trace/i.test(text)) return true;
  if (/stock_business_profile|business_role_summary_json|evidence_json|impact_json_raw|reason_code|deterministic|precision gate|direct_entity_lookup|profile_recall/i.test(text)) return true;
  if (/^(stock mapped|sector only|macro only|no actionable mapping|event action|event review|event watch)$/i.test(normalized)) return true;
  return false;
}

function publicText(value: any): string {
  const text = cleanText(value)
    .replace(/DIRECT_ENTITY_BUSINESS_PROFILE_MATERIAL_MATCHED/gi, "직접 영향")
    .replace(/DIRECT_ENTITY_BUSINESS_PROFILE_REVIEW_MATCHED/gi, "검토 후보")
    .replace(/DIRECT_ENTITY_ECONOMIC_BRIDGE_PROFILE_REVIEW/gi, "검토 후보")
    .replace(/DIRECT_ENTITY_PROFILE_WEAK_WATCH/gi, "관찰 후보")
    .replace(/EXPOSURE_WATCH_DYNAMIC_CONTEXT_MATCHED/gi, "관찰 후보")
    .replace(/direct entity/gi, "직접 등장 기업")
    .replace(/business profile/gi, "사업 근거")
    .replace(/stock_business_profile/gi, "사업 근거")
    .replace(/mapped/g, "연결")
    .replace(/\s+/g, " ")
    .trim();

  if (isInternalLine(text)) return "";
  return text;
}

function userReason(value: any, group?: CandidateGroup): string {
  const raw = publicText(value);
  if (!raw) {
    if (group === "direct") return "기사에 직접 등장한 기업이며 사업 연관성이 확인됩니다.";
    if (group === "review") return "기사와 사업 영역이 연결되어 추가 확인이 필요합니다.";
    if (group === "watch") return "직접 등장 기업은 아니지만 관련 테마 노출 가능성이 있습니다.";
    return "";
  }

  return raw
    .replace(/직접 등장 회사\s*\+\s*경제적 이벤트\s*\+\s*사업보고서 사업 객체 연결 확인/g, "기사에 직접 등장한 기업이며 사업 연관성이 확인됩니다.")
    .replace(/직접 등장 회사와 경제적 이벤트는 확인되나 사업 객체 근거가 Direct 수준까지 강하지 않아 Review로 분류/g, "직접 등장 기업이지만 영향 강도는 추가 확인이 필요합니다.")
    .replace(/직접 등장 회사이나 수주·매출 등 Direct 확정 근거는 부족하여 Review로 분류/g, "직접 등장 기업이지만 수주·매출 확정 이벤트는 아니므로 검토 후보입니다.")
    .replace(/직접 등장 회사이나 사업보고서 객체 일치가 약해 관찰로 제한/g, "직접 등장 기업이지만 사업 연결 강도가 낮아 관찰 후보입니다.")
    .replace(/기사 직접 등장 회사는 아니지만 이슈 객체와 사업보고서 근거의 문맥 일치가 확인되어 사업 노출 후보로 표시/g, "기사에 직접 등장한 기업은 아니지만 관련 사업 노출 가능성이 있습니다.")
    .replace(/\s+/g, " ")
    .trim();
}

function stripBoilerplate(value: any): string {
  return cleanText(value)
    .replace(/^\[\s*분석\s*대상\s*기사\s*\]\s*/i, "")
    .replace(/^제목\s*[:：]\s*/i, "")
    .replace(/\s*원본\s*URL\s*[:：].*$/i, "")
    .replace(/\s*URL\s*[:：]\s*https?:\/\/\S+.*$/i, "")
    .replace(/\s*발행일\s*[:：]\s*\d{4}[-.]\d{1,2}[-.]\d{1,2}[^출처요약]*/i, " ")
    .replace(/\s*출처\s*[:：]\s*[^요약]{1,90}(?=\s*(?:기준\s*)?요약\s*[:：]|$)/i, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractTitle(value: any): string {
  const raw = stripBoilerplate(value);
  if (!raw) return "";
  return cleanText(
    raw.match(
      /(?:제목\s*[:：]\s*)?(.+?)(?=\s*(?:기준\s*)?요약\s*[:：]|\s*출처\s*[:：]|\s*발행일\s*[:：]|\s*원본\s*URL\s*[:：]|$)/i,
    )?.[1] ?? raw,
  )
    .replace(/\s*(?:기준\s*)?요약\s*[:：].*$/i, "")
    .replace(/\s*출처\s*[:：].*$/i, "")
    .replace(/\s*발행일\s*[:：].*$/i, "")
    .trim();
}

function extractSummary(value: any): string {
  const raw = stripBoilerplate(value);
  if (!raw) return "";
  return cleanText(
    raw.match(
      /(?:기준\s*)?요약\s*[:：]\s*(.+?)(?=\s*출처\s*[:：]|\s*발행일\s*[:：]|\s*원본\s*URL\s*[:：]|$)/i,
    )?.[1] ?? raw,
  )
    .replace(/^제목\s*[:：].+?(?:기준\s*)?요약\s*[:：]\s*/i, "")
    .replace(/\s*출처\s*[:：].*$/i, "")
    .replace(/\s*발행일\s*[:：].*$/i, "")
    .replace(/\s*원본\s*URL\s*[:：].*$/i, "")
    .trim();
}

function sectorLabel(value: any): string {
  const raw = cleanText(value);
  const key = raw.toUpperCase();
  const map: Record<string, string> = {
    SEMICONDUCTOR: "",
    SEM: "",
    MEMORY: "메모리",
    AI: "AI",
    DEFENSE: "방산",
    DEFENSE_AEROSPACE: "방산/항공",
    SHIPBUILDING: "조선",
    AUTOMOTIVE: "자동차",
    EV: "전기차",
    BATTERY: "배터리",
    BIO_HEALTHCARE: "바이오/헬스케어",
    CHIP: "",
    MACRO: "매크로",
    POLICY: "정책",
  };
  if (raw === "반도체" || raw === "칩/반도체" || raw === "반도체/칩/수출") return "";
  return map[key] ?? raw;
}

function getParsed(feed: any) {
  const result =
    parseJsonLoose(feed?.result_json) ??
    parseJsonLoose(feed?.resultJson) ??
    parseJsonLoose(feed?.analysis_result) ??
    parseJsonLoose(feed?.analysisResult) ??
    {};
  const gpt =
    parseJsonLoose(feed?.gpt_analysis) ??
    parseJsonLoose(feed?.gptAnalysis) ??
    feed?.gpt_analysis ??
    feed?.gptAnalysis ??
    {};
  const ticker =
    parseJsonLoose(feed?.ticker_insight) ??
    parseJsonLoose(feed?.tickerInsight) ??
    parseJsonLoose(gpt?.ticker_insight) ??
    parseJsonLoose(gpt?.tickerInsight) ??
    feed?.ticker_insight ??
    feed?.tickerInsight ??
    gpt?.ticker_insight ??
    gpt?.tickerInsight ??
    {};
  const impact =
    parseJsonLoose(feed?.impact_json_raw) ??
    parseJsonLoose(feed?.impactJsonRaw) ??
    parseJsonLoose(gpt?.impact_json_raw) ??
    parseJsonLoose(gpt?.impactJsonRaw) ??
    feed?.impact_json_raw ??
    feed?.impactJsonRaw ??
    gpt?.impact_json_raw ??
    gpt?.impactJsonRaw ??
    result ??
    {};
  const final = result?.final ?? ticker?.final ?? impact?.final ?? gpt?.final ?? {};
  const articleImpact = final?.article_impact ?? final?.articleImpact ?? result?.article_impact ?? ticker?.article_impact ?? impact?.article_impact ?? {};
  return { result, gpt, ticker, impact, final, articleImpact };
}

function getImpactDirection(feed: any, parsed?: ReturnType<typeof getParsed>): "positive" | "negative" | "neutral" {
  const p = parsed ?? getParsed(feed);
  const raw = norm(
    firstNonEmpty(
      feed?.impact_direction,
      feed?.impactDirection,
      p.articleImpact?.impact_direction,
      p.articleImpact?.impactDirection,
      p.final?.impact_direction,
      p.result?.impact_direction,
      "",
    ),
  );
  if (raw.includes("negative") || raw.includes("부정") || raw.includes("악재") || raw.includes("risk")) return "negative";
  if (raw.includes("positive") || raw.includes("긍정") || raw.includes("호재")) return "positive";
  return "neutral";
}

function directionLabel(direction: "positive" | "negative" | "neutral"): string {
  if (direction === "positive") return "긍정";
  if (direction === "negative") return "부정";
  return "중립";
}

function getImpactScore(feed: any, parsed?: ReturnType<typeof getParsed>): number {
  const p = parsed ?? getParsed(feed);
  return clamp100(
    firstNonEmpty(
      feed?.impact_score,
      feed?.impactScore,
      p.result?.impact_score,
      p.final?.impact_score,
      p.articleImpact?.impact_score,
      p.ticker?.impact_score,
      p.impact?.impact_score,
      0,
    ),
  );
}

function getFinalMode(feed: any, parsed?: ReturnType<typeof getParsed>): string {
  const p = parsed ?? getParsed(feed);
  return String(
    feed?.event_final_mode ??
      feed?.eventFinalMode ??
      p.result?.event_final_mode ??
      p.result?.eventFinalMode ??
      p.final?.event_final_mode ??
      p.final?.eventFinalMode ??
      p.ticker?.event_final_mode ??
      p.ticker?.eventFinalMode ??
      p.impact?.event_final_mode ??
      "",
  );
}

function candidateDecision(raw: any): CandidateGroup {
  const text = norm(
    firstNonEmpty(
      raw?.tier,
      raw?.mapping_decision,
      raw?.mappingDecision,
      raw?.decision,
      raw?.mapping_bucket,
      raw?.mappingBucket,
      raw?.relation_tier,
      raw?.match_type,
      raw?.matchType,
      raw?.role,
    ),
  );
  if (text.includes("exposure") || text.includes("watch") || text.includes("관찰")) return "watch";
  if (text.includes("review") || text.includes("검토")) return "review";
  if (
    text.includes("direct") ||
    text.includes("action") ||
    text.includes("직접") ||
    text === "mapped" ||
    text.includes("stock mapped")
  ) return "direct";
  if (raw?.is_direct_stock_mapping || raw?.isDirectStockMapping) return "direct";
  return "watch";
}

function normalizeCandidate(raw: any, fallback?: CandidateGroup): StockCandidate | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    const ticker = normalizeTicker(raw);
    const name = cleanText(raw.replace(/\(?A?\d{6}\)?/g, "")) || ticker;
    if (!ticker || !name) return null;
    return { name, corp_name: name, ticker, stock_code: ticker, mapping_decision: fallback ?? "watch" };
  }
  if (typeof raw !== "object") return null;

  const ticker = normalizeTicker(raw.ticker ?? raw.stock_code ?? raw.stockCode ?? raw.code);
  const name = cleanText(raw.corp_name ?? raw.corpName ?? raw.name ?? raw.company_name ?? raw.companyName ?? ticker);
  if (!ticker || !name) return null;

  const group = fallback ?? candidateDecision(raw);
  return {
    ...raw,
    name,
    corp_name: name,
    ticker,
    stock_code: ticker,
    mapping_decision: group,
    matched_terms: raw.matched_terms ?? raw.matchedTerms,
    strong_terms: raw.strong_terms ?? raw.strongTerms,
    support_terms: raw.support_terms ?? raw.supportTerms,
  };
}

function isBlockedCandidate(candidate: StockCandidate): boolean {
  if (!normalizeTicker(candidate.ticker ?? candidate.stock_code)) return true;
  if (candidate.user_visible === false) return true;
  const decision = norm(candidate.mapping_decision ?? candidate.decision ?? "");
  if (decision.includes("exclude") || decision.includes("제외")) return true;
  const reasonCode = String(candidate.reason_code ?? "").trim();
  if (INTERNAL_REASON_CODES.has(reasonCode)) return true;
  if (/BLOCKED|DISABLED|AMBIGUOUS|EXCLUDE_CAPPED|WEAK_ENTITY/i.test(reasonCode)) return true;
  return false;
}


function candidateHasRealName(candidate: StockCandidate): boolean {
  const ticker = normalizeTicker(candidate.ticker ?? candidate.stock_code) ?? "";
  const name = cleanText(candidate.corp_name ?? candidate.corpName ?? candidate.name);
  if (!name) return false;
  return name !== ticker && !/^\d{6}$/.test(name);
}

function candidateRichness(candidate: StockCandidate): number {
  let score = 0;
  if (candidateHasRealName(candidate)) score += 100;
  if (cleanText(candidate.reason)) score += 20;
  if (asArrayLoose(candidate.matched_terms ?? candidate.matchedTerms).length) score += 10;
  if (asArrayLoose(candidate.strong_terms ?? candidate.strongTerms).length) score += 10;
  if (asArrayLoose(candidate.support_terms ?? candidate.supportTerms).length) score += 6;
  if (candidate.is_direct_stock_mapping || candidate.isDirectStockMapping) score += 8;
  if (candidateDecision(candidate) === "direct") score += 5;
  return score;
}

function mergeCandidate(existing: StockCandidate, incoming: StockCandidate): StockCandidate {
  const preferred =
    candidateRichness(incoming) > candidateRichness(existing)
      ? incoming
      : existing;
  const secondary = preferred === incoming ? existing : incoming;

  const ticker = normalizeTicker(
    preferred.ticker ??
    preferred.stock_code ??
    secondary.ticker ??
    secondary.stock_code,
  );

  const preferredName = cleanText(
    preferred.corp_name ??
    preferred.corpName ??
    preferred.name,
  );
  const secondaryName = cleanText(
    secondary.corp_name ??
    secondary.corpName ??
    secondary.name,
  );
  const name =
    (preferredName && preferredName !== ticker ? preferredName : "") ||
    (secondaryName && secondaryName !== ticker ? secondaryName : "") ||
    preferredName ||
    secondaryName ||
    ticker ||
    "";

  const preferredGroup = candidateDecision(preferred);
  const secondaryGroup = candidateDecision(secondary);
  const group: CandidateGroup =
    preferredGroup === "direct" || secondaryGroup === "direct"
      ? "direct"
      : preferredGroup === "review" || secondaryGroup === "review"
        ? "review"
        : "watch";

  return {
    ...secondary,
    ...preferred,
    ticker,
    stock_code: ticker,
    name,
    corp_name: name,
    corpName: name,
    mapping_decision: group,
    mappingDecision: group,
    matched_terms:
      preferred.matched_terms ??
      preferred.matchedTerms ??
      secondary.matched_terms ??
      secondary.matchedTerms,
    strong_terms:
      preferred.strong_terms ??
      preferred.strongTerms ??
      secondary.strong_terms ??
      secondary.strongTerms,
    support_terms:
      preferred.support_terms ??
      preferred.supportTerms ??
      secondary.support_terms ??
      secondary.supportTerms,
    reason: cleanText(preferred.reason) || cleanText(secondary.reason) || undefined,
  };
}

function collectCandidates(feed: any, parsed: ReturnType<typeof getParsed>): StockCandidate[] {
  const impactLabel = norm(
    firstNonEmpty(
      feed?.impactLabel,
      feed?.impact_label,
      parsed.result?.impactLabel,
      parsed.result?.impact_label,
      parsed.final?.impactLabel,
      parsed.final?.impact_label,
    ),
  );
  const compactRelatedFallback: CandidateGroup =
    impactLabel.includes("stock mapped") || impactLabel.includes("stock_mapped")
      ? "direct"
      : "watch";

  const sources: Array<{ rows: any[]; fallback?: CandidateGroup }> = [
    {
      rows: asArrayLoose(feed?.relatedStocks ?? feed?.related_stocks),
      fallback: compactRelatedFallback,
    },
    {
      rows: asArrayLoose(feed?.relatedTickers ?? feed?.related_tickers),
      fallback: compactRelatedFallback,
    },
    { rows: asArrayLoose(feed?.direct_mapped_stocks ?? feed?.directMappedStocks ?? parsed.result?.direct_mapped_stocks ?? parsed.ticker?.direct_mapped_stocks), fallback: undefined },
    { rows: asArrayLoose(feed?.exposure_watch_candidates ?? feed?.exposureWatchCandidates ?? parsed.result?.exposure_watch_candidates ?? parsed.ticker?.exposure_watch_candidates), fallback: "watch" },
    { rows: asArrayLoose(feed?.mapped_ticker_rows_json ?? feed?.mappedTickerRowsJson ?? feed?.mapped_tickers_json ?? feed?.mappedTickersJson), fallback: undefined },
    { rows: asArrayLoose(feed?.mapped_ticker_rows ?? feed?.mappedTickerRows), fallback: undefined },
    { rows: asArrayLoose(parsed.ticker?.mapped_ticker_rows ?? parsed.ticker?.mappedTickerRows), fallback: undefined },
    { rows: asArrayLoose(parsed.impact?.mapped_ticker_rows ?? parsed.impact?.mappedTickerRows), fallback: undefined },
    { rows: asArrayLoose(parsed.ticker?.event_unit_visible_stocks ?? feed?.event_unit_visible_stocks), fallback: undefined },
    { rows: asArrayLoose(parsed.ticker?.direct_candidates), fallback: "direct" },
    { rows: asArrayLoose(parsed.ticker?.review_candidates), fallback: "review" },
    { rows: asArrayLoose(parsed.ticker?.watch_candidates ?? parsed.ticker?.watch), fallback: "watch" },
    { rows: asArrayLoose(parsed.ticker?.direct_tickers), fallback: "direct" },
    { rows: asArrayLoose(parsed.ticker?.indirect_tickers), fallback: "review" },
    { rows: asArrayLoose(parsed.ticker?.related_tickers), fallback: "watch" },
  ];

  const candidateByTicker = new Map<string, StockCandidate>();
  const tickerOrder: string[] = [];

  sources.forEach(({ rows, fallback }) => {
    rows.forEach((row) => {
      const candidate = normalizeCandidate(row, fallback);
      if (!candidate || isBlockedCandidate(candidate)) return;

      const key = normalizeTicker(candidate.ticker ?? candidate.stock_code);
      if (!key) return;

      const existing = candidateByTicker.get(key);
      if (!existing) {
        candidateByTicker.set(key, candidate);
        tickerOrder.push(key);
        return;
      }

      // 동일 ticker가 문자열 배열과 상세 객체에 동시에 존재할 수 있다.
      // 종목명이 ticker 숫자로만 들어온 항목보다 corp_name/name을 가진 상세 객체를 우선한다.
      candidateByTicker.set(key, mergeCandidate(existing, candidate));
    });
  });

  return tickerOrder
    .map((ticker) => candidateByTicker.get(ticker))
    .filter((candidate): candidate is StockCandidate => Boolean(candidate));
}

function bucketCandidates(candidates: StockCandidate[]): CandidateBuckets {
  const direct: StockCandidate[] = [];
  const review: StockCandidate[] = [];
  const watch: StockCandidate[] = [];

  candidates.forEach((candidate) => {
    const group = candidateDecision(candidate);
    if (group === "direct") direct.push(candidate);
    else if (group === "review") review.push(candidate);
    else watch.push(candidate);
  });

  return { direct, review, watch, visible: [...direct, ...review, ...watch] };
}

function getTerms(candidate: StockCandidate): string[] {
  const out: string[] = [];
  const visit = (value: any) => {
    const parsed = parseJsonLoose(value) ?? value;
    if (!parsed) return;
    if (Array.isArray(parsed)) {
      parsed.forEach(visit);
      return;
    }
    if (typeof parsed === "object") {
      Object.values(parsed).forEach(visit);
      return;
    }
    const text = cleanText(parsed);
    if (!text || isInternalLine(text) || text.length > 34 || /[{}[\]|]/.test(text)) return;
    if (!out.includes(text)) out.push(text);
  };
  [candidate.matched_terms, candidate.strong_terms, candidate.support_terms].forEach(visit);
  return out.slice(0, 8);
}

function candidateName(candidate: StockCandidate): string {
  return cleanText(candidate.corp_name ?? candidate.corpName ?? candidate.name ?? candidate.ticker ?? "후보");
}

function candidateKey(candidate: StockCandidate): string {
  const ticker = normalizeTicker(candidate.ticker ?? candidate.stock_code ?? candidate.stockCode ?? candidate.code);
  if (ticker) return ticker;

  const name = cleanText(candidate.corp_name ?? candidate.corpName ?? candidate.name);
  if (name) return name;

  const bucket = cleanText(candidate.mapping_bucket ?? candidate.mappingBucket ?? candidate.mapping_decision ?? candidate.mappingDecision ?? candidate.tier);
  return bucket || "candidate";
}

function groupLabel(group: CandidateGroup): string {
  if (group === "direct") return "직접 영향";
  if (group === "review") return "검토 후보";
  return "관찰 후보";
}

function getTitle(feed?: FeedDetailItem | null): string {
  if (!feed) return "";
  const raw = firstNonEmpty(
    (feed as any).title_ko,
    (feed as any).titleKo,
    (feed as any).title,
    (feed as any).headline,
  );
  return extractTitle(raw) || cleanText(raw) || "(제목 없음)";
}

function getSummary(feed?: FeedDetailItem | null, parsed?: ReturnType<typeof getParsed>): string {
  if (!feed) return "";
  const p = parsed ?? getParsed(feed);
  const raw = firstNonEmpty(
    (feed as any).summary_full,
    (feed as any).summary_ko,
    (feed as any).summaryKo,
    (feed as any).summary,
    p.result?.summary,
    p.gpt?.summary,
    p.final?.impact_reason,
    p.articleImpact?.impact_reason,
    (feed as any).description,
  );
  const summary = extractSummary(raw);
  if (summary && summary !== getTitle(feed)) return summary;
  return summary || "";
}

function getSource(feed?: FeedDetailItem | null): string {
  if (!feed) return "출처 미상";
  return cleanText(firstNonEmpty((feed as any).source_name, (feed as any).sourceName, (feed as any).source, (feed as any).publisher, "출처 미상"));
}

function getDateLabel(feed?: FeedDetailItem | null): string {
  if (!feed) return "";
  const raw = String(firstNonEmpty((feed as any).published_at, (feed as any).publishedAt, (feed as any).created_at, (feed as any).createdAt, ""));
  const d = dayjs(raw);
  return d.isValid() ? d.format("YYYY.MM.DD HH:mm") : "";
}

function normalizeSourceUrl(value: any): string {
  const raw = String(value ?? "")
    .replace(/&amp;/gi, "&")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();

  if (!raw) return "";

  const isBusinessKoreaUrl = (() => {
    try {
      const parsed = new URL(raw);
      const hostname = parsed.hostname.toLowerCase();
      return hostname === "businesskorea.co.kr" || hostname.endsWith(".businesskorea.co.kr");
    } catch {
      return /^https?:\/\/(?:www\.)?businesskorea\.co\.kr(?:\/|$)/i.test(raw);
    }
  })();

  if (!isBusinessKoreaUrl) return raw;

  return raw
    // 잘못 수집된 ?idxno%20274099 또는 ?idxno 274099을 정상 쿼리로 복구한다.
    .replace(/([?&])idxno(?:%20|\s+)(?=\d)/gi, "$1idxno=")
    // 이중 인코딩 또는 문자열 저장 과정에서 =가 %3D로 남은 경우도 복구한다.
    .replace(/([?&])idxno%3D(?=\d)/gi, "$1idxno=");
}

function getSourceUrl(feed?: FeedDetailItem | null): string {
  if (!feed) return "";

  return normalizeSourceUrl(
    firstNonEmpty(
      (feed as any).source_url,
      (feed as any).sourceUrl,
      (feed as any).url,
      "",
    ),
  );
}

function getTags(feed: any, parsed: ReturnType<typeof getParsed>): string[] {
  const raw =
    parseJsonLoose(feed?.issue_tags) ??
    parseJsonLoose(feed?.issueTags) ??
    parseJsonLoose(parsed.gpt?.issue_tags) ??
    parseJsonLoose(parsed.ticker?.issue_tags) ??
    feed?.issue_tags ??
    feed?.issueTags ??
    [];
  const source = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/[,|]/) : [];
  return source
    .map((x) => sectorLabel(x))
    .filter((x) => x && !isInternalLine(x))
    .slice(0, 8);
}

function buildVM(feed?: FeedDetailItem | null): DetailVM | null {
  if (!feed) return null;

  const parsed = getParsed(feed);
  const candidates = collectCandidates(feed, parsed);
  const buckets = bucketCandidates(candidates);
  const impactDirection = getImpactDirection(feed, parsed);
  const summary = getSummary(feed, parsed);
  const impactReason =
    userReason(
      firstNonEmpty(
        parsed.final?.impact_reason,
        parsed.articleImpact?.impact_reason,
        parsed.result?.impact_reason,
        parsed.gpt?.impact_reason,
        summary,
      ),
    ) || summary;

  const mode = getFinalMode(feed, parsed).toUpperCase();
  const modeLabel =
    buckets.direct.length > 0
      ? "직접 영향"
      : buckets.review.length > 0
        ? "검토 후보"
        : buckets.watch.length > 0
          ? "관찰 후보"
          : mode.includes("MACRO")
            ? "매크로 참고"
            : mode.includes("SECTOR")
              ? "시장 참고"
              : "매핑 없음";

  return {
    title: getTitle(feed),
    summary,
    source: getSource(feed),
    dateLabel: getDateLabel(feed),
    sourceUrl: getSourceUrl(feed),
    impactScore: getImpactScore(feed, parsed),
    impactDirection,
    impactLabel: directionLabel(impactDirection),
    impactReason,
    modeLabel,
    sectorLabel: "",
    canonicalSector: "",
    tags: getTags(feed, parsed),
    buckets,
  };
}






function toneClass(tone: Tone | CandidateGroup | "positive" | "negative" | "neutral" | undefined): string {
  if (tone === "direct") return "is-direct";
  if (tone === "review") return "is-review";
  if (tone === "watch") return "is-watch";
  if (tone === "risk" || tone === "negative") return "is-risk";
  if (tone === "positive") return "is-positive";
  return "is-neutral";
}

function detailTone(vm: DetailVM): Tone {
  if (vm.buckets.direct.length) return "direct";
  if (vm.buckets.review.length) return "review";
  if (vm.buckets.watch.length) return "watch";
  if (vm.impactDirection === "negative") return "risk";
  if (vm.impactDirection === "positive") return "positive";
  return "neutral";
}

function Pill({ children, tone = "neutral" }: { children?: React.ReactNode; tone?: Tone | CandidateGroup }) {
  return <span className={`fd-pill ${toneClass(tone)}`}>{children}</span>;
}

function FeedDetailDialog({
  open,
  onClose,
  feed,
  loading = false,
  error = null,
  onAlert,
}: FeedDetailDialogProps) {
  const [snack, setSnack] = useState("");
  const vm = useMemo(() => buildVM(feed), [feed]);

  const eventId = String(
    firstNonEmpty(
      (feed as any)?.id,
      (feed as any)?.feed_id,
      (feed as any)?.source_feed_id,
      "",
    ),
  );

  const handleCopy = async () => {
    if (!vm) return;
    const text = [
      vm.title,
      vm.summary,
      `영향 방향: ${vm.impactLabel}`,
      `영향도: ${Math.round(vm.impactScore)}`,
      vm.impactReason,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setSnack("요약을 복사했습니다.");
    } catch {
      setSnack("복사에 실패했습니다.");
    }
  };

  const handleAlert = async () => {
    if (!onAlert || !eventId) return;
    await onAlert(eventId);
    setSnack("알림 설정을 요청했습니다.");
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ className: "detail-dialog-paper fd-premium-paper" }}
      >
        <DialogContent className="fd-premium-content">
          <div className="fd-shell">
            <header className="fd-command-bar">
              <div className="fd-command-title">
                <span>이슈 리포트</span>
                <strong>{vm?.title || "불러오는 중"}</strong>
              </div>
              <div className="fd-command-actions">
                {vm?.sourceUrl ? (
                  <IconButton size="small" component="a" href={vm.sourceUrl} target="_blank" rel="noreferrer" aria-label="원문 열기">
                    <OpenInNewOutlined fontSize="small" />
                  </IconButton>
                ) : null}
                <IconButton size="small" onClick={handleCopy} aria-label="요약 복사">
                  <ContentCopyOutlined fontSize="small" />
                </IconButton>
                {onAlert ? (
                  <IconButton size="small" onClick={handleAlert} aria-label="알림 설정">
                    <AddAlertOutlined fontSize="small" />
                  </IconButton>
                ) : null}
                <IconButton size="small" onClick={onClose} aria-label="닫기">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </header>

            {loading ? (
              <div className="fd-state"><CircularProgress /><p>이슈를 불러오는 중입니다.</p></div>
            ) : error ? (
              <div className="fd-state"><Alert severity="error">{error}</Alert></div>
            ) : !vm ? (
              <div className="fd-state"><Alert severity="info">표시할 이슈가 없습니다.</Alert></div>
            ) : (
              <article className="fd-report">
                <section className={`fd-hero ${toneClass(detailTone(vm))}`}>
                  <div className="fd-hero-meta">
                    <Pill tone={vm.impactDirection === "negative" ? "risk" : vm.impactDirection === "positive" ? "positive" : "neutral"}>{vm.impactLabel}</Pill>
                    <Pill tone={detailTone(vm)}>{vm.modeLabel}</Pill>
                    {vm.dateLabel ? <span>{vm.dateLabel}</span> : null}
                  </div>
                  <h1>{vm.title}</h1>
                  <p>{vm.summary || vm.impactReason || "요약 정보가 없습니다."}</p>
                  <div className="fd-hero-grid">
                    <HeroMetric label="영향도" value={String(Math.round(vm.impactScore))} tone={detailTone(vm)} />
                    <HeroMetric label="직접" value={String(vm.buckets.direct.length)} tone="direct" />
                    <HeroMetric label="검토" value={String(vm.buckets.review.length)} tone="review" />
                    <HeroMetric label="관찰" value={String(vm.buckets.watch.length)} tone="watch" />
                  </div>
                </section>

                <div className="fd-report-grid">
                  <main className="fd-report-main">
                    <ReportBlock title="핵심 요약">
                      <p className="fd-body-text">{vm.summary || vm.impactReason || "요약 정보가 없습니다."}</p>
                    </ReportBlock>

                    <ReportBlock title="영향 판단">
                      <div className="fd-judgement">
                        <InfoTile label="방향" value={vm.impactLabel} tone={vm.impactDirection === "negative" ? "risk" : vm.impactDirection === "positive" ? "positive" : "neutral"} />
                        <InfoTile label="분류" value={vm.modeLabel} tone={detailTone(vm)} />
                        <InfoTile label="점수" value={String(Math.round(vm.impactScore))} tone="direct" />
                      </div>
                      <p className="fd-body-text">{vm.impactReason || "영향 판단 근거를 확인 중입니다."}</p>
                    </ReportBlock>

                    <CandidateGroupBlock title="직접 영향 종목" group="direct" candidates={vm.buckets.direct} empty="직접 영향으로 확정된 종목은 없습니다." />
                    <CandidateGroupBlock title="검토 후보" group="review" candidates={vm.buckets.review} empty="추가 확인이 필요한 후보는 없습니다." />
                    <CandidateGroupBlock title="관찰 후보" group="watch" candidates={vm.buckets.watch} empty="관찰 후보가 없습니다." />
                  </main>

                  <aside className="fd-report-side">
                    <ReportBlock title="출처">
                      <MetaLine label="매체" value={vm.source} />
                      <MetaLine label="발행" value={vm.dateLabel || "확인 필요"} />
                      {vm.sourceUrl ? (
                        <a href={vm.sourceUrl} target="_blank" rel="noreferrer" className="fd-source-link">
                          원문 열기 <OpenInNewOutlined fontSize="small" />
                        </a>
                      ) : null}
                    </ReportBlock>

                    <ReportBlock title="키워드">
                      <div className="fd-keywords">
                        {vm.tags.length ? vm.tags.map((tag) => <Pill key={tag}>{tag}</Pill>) : <span className="fd-muted">표시할 키워드가 없습니다.</span>}
                      </div>
                    </ReportBlock>

                    <ReportBlock title="표시 기준">
                      <Guideline title="직접 영향" desc="원문에 등장한 기업이고 사업 연관성이 확인된 경우" tone="direct" />
                      <Guideline title="검토 후보" desc="등장은 확인됐지만 수주·매출 확정까지는 아닌 경우" tone="review" />
                      <Guideline title="관찰 후보" desc="직접 등장은 아니지만 관련 사업 노출 가능성이 있는 경우" tone="watch" />
                    </ReportBlock>
                  </aside>
                </div>
              </article>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={2200} onClose={() => setSnack("") }>
        <Alert severity="success" variant="filled">{snack}</Alert>
      </Snackbar>
    </>
  );
}

function HeroMetric({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return <div className={`fd-hero-metric ${toneClass(tone)}`}><span>{label}</span><strong>{value}</strong></div>;
}

function ReportBlock({ title, children }: { title: string; children?: React.ReactNode }) {
  return <section className="fd-block"><h2>{title}</h2>{children}</section>;
}

function InfoTile({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return <div className={`fd-info-tile ${toneClass(tone)}`}><span>{label}</span><strong>{value}</strong></div>;
}

function CandidateGroupBlock({ title, group, candidates, empty }: { title: string; group: CandidateGroup; candidates: StockCandidate[]; empty: string }) {
  return (
    <ReportBlock title={`${title}${candidates.length ? ` ${candidates.length}` : ""}`}>
      {candidates.length ? <div className="fd-candidate-list">{candidates.map((candidate) => <CandidateCard key={`${candidateKey(candidate)}-${group}`} candidate={candidate} group={group} />)}</div> : <p className="fd-body-text">{empty}</p>}
    </ReportBlock>
  );
}

function CandidateCard({ candidate, group }: { candidate: StockCandidate; group: CandidateGroup }) {
  const ticker = normalizeTicker(candidate.ticker ?? candidate.stock_code);
  const terms = getTerms(candidate);
  const reason = userReason(candidate.reason, group);
  return (
    <article className={`fd-candidate-card ${toneClass(group)}`}>
      <div className="fd-candidate-head">
        <div>
          <strong>{candidateName(candidate)}</strong>
          {ticker ? <span>{ticker}</span> : null}
        </div>
        <Pill tone={group}>{groupLabel(group)}</Pill>
      </div>
      <p>{reason}</p>
      {terms.length ? <div className="fd-term-row">{terms.map((term) => <Pill key={term}>{term}</Pill>)}</div> : null}
    </article>
  );
}

function MetaLine({ label, value }: { label: string; value: string }) {
  return <div className="fd-meta-line"><span>{label}</span><strong>{value}</strong></div>;
}

function Guideline({ title, desc, tone }: { title: string; desc: string; tone: Tone }) {
  return <div className="fd-guideline"><Pill tone={tone}>{title}</Pill><p>{desc}</p></div>;
}

export default FeedDetailDialog;
